/**
 * DecisionFlow — TrustCenter
 * ui/src/components/admin/TrustCenter.jsx
 * S438 — OI-WFL-TRUST-CENTER-UI
 *
 * Governance management page for Settings / Admin section.
 * Consumes GET /api/v2/agent/trust-center (implemented S437).
 * Accessible to comp_admin + tenant_super_admin only (enforced server-side).
 *
 * Design rationale:
 *   - Read-only surface — no writes, no forms, no modals
 *   - No Claude call on this page (spec §11.2 explicit requirement)
 *   - Kill switch status is the primary governance signal — placed first in sidebar
 *   - schema_note rendered as an inline info note (DDL r45 gap, no surface column yet)
 *   - All data from live API — no cached, estimated, or computed values
 *
 * Layout: mirrors SetupGuide.jsx — lg:grid-cols-3, main lg:col-span-2, sidebar 1 col.
 *
 * Sections (spec §11.2):
 *   1. Kill Switch Status         — governance_posture.kill_switch
 *   2. Model Configuration        — model_config
 *   3. Agent Activity             — activity_summary (30-day window)
 *   4. Recent Agent Sessions      — recent_sessions (max 10, no PII)
 *   5. Permanent Constraints      — governance_posture.permanent_constraints
 *   6. Tool Manifest              — governance_posture.tool_manifest
 *   7. Data Boundary              — data_boundary
 */

import { useState, useEffect, useCallback } from "react";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Clock,
  Database,
  EyeOff,
  Info,
  KeyRound,
  RefreshCw,
  Server,
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShieldOff,
  Sparkles,
  XCircle,
} from "lucide-react";

import Card   from "../shared/Card.jsx";
import Pill   from "../shared/Pill.jsx";
import Button from "../shared/Button.jsx";
import AIGovernancePanel from "./AIGovernancePanel.jsx";

const API_BASE = "/api/v2";

// ---------------------------------------------------------------------------
// Shared primitives — identical pattern to SetupGuide.jsx
// ---------------------------------------------------------------------------

function LoadingRow() {
  return (
    <div className="animate-pulse space-y-2">
      <div className="h-4 w-2/3 rounded bg-slate-200 dark:bg-slate-700" />
      <div className="h-4 w-1/2 rounded bg-slate-200 dark:bg-slate-700" />
    </div>
  );
}

function ErrorNote({ message }) {
  return (
    <div className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950 dark:text-red-300">
      {message ?? "Unable to load data."}
    </div>
  );
}

function InfoNote({ children }) {
  return (
    <div className="flex items-start gap-2 rounded-xl border border-blue-100 bg-blue-50 px-3 py-2.5 dark:border-blue-900 dark:bg-blue-950/40">
      <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" />
      <p className="text-xs text-blue-700 dark:text-blue-300">{children}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch {
    return iso;
  }
}

function truncateId(id) {
  if (!id) return "—";
  return id.length > 12 ? `${id.slice(0, 8)}…` : id;
}

// ---------------------------------------------------------------------------
// Kill switch helpers
// ---------------------------------------------------------------------------

/**
 * Derive a single posture from the kill_switch object.
 * Handles both array-of-scopes and nested-object shapes defensively.
 * Returns: "clear" | "restricted" | "platform_restricted"
 */
function deriveKillSwitchPosture(ks) {
  if (!ks || typeof ks !== "object") return "clear";

  // Shape A: { any_active: bool, scopes: [...] }
  if ("any_active" in ks) {
    if (!ks.any_active) return "clear";
    const hasPlatform = (ks.scopes ?? []).some(
      (s) => s.scope === "platform" && s.active
    );
    return hasPlatform ? "platform_restricted" : "restricted";
  }

  // Shape B: { platform: {active}, tenant: {active}, ... }
  const entries = Object.entries(ks).filter(([, v]) => v && typeof v === "object");
  const anyActive = entries.some(([, v]) => v.active === true);
  if (!anyActive) return "clear";
  const platformActive = ks.platform?.active === true;
  return platformActive ? "platform_restricted" : "restricted";
}

function KillSwitchBadge({ posture }) {
  if (posture === "clear") {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5 dark:border-emerald-900 dark:bg-emerald-950/40">
        <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-500" />
        <span className="text-sm font-medium text-emerald-700 dark:text-emerald-400">
          No active restrictions — agent operating normally
        </span>
      </div>
    );
  }
  if (posture === "platform_restricted") {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 dark:border-red-900 dark:bg-red-950/40">
        <ShieldOff className="h-4 w-4 shrink-0 text-red-500" />
        <span className="text-sm font-medium text-red-700 dark:text-red-400">
          Platform kill switch active — all agent calls blocked
        </span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 dark:border-amber-900 dark:bg-amber-950/40">
      <ShieldAlert className="h-4 w-4 shrink-0 text-amber-500" />
      <span className="text-sm font-medium text-amber-700 dark:text-amber-400">
        Kill switch active at tenant or domain scope
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Completion reason display
// ---------------------------------------------------------------------------

const REASON_TONE = {
  completed:              "Complete",
  audit_failure:          "Blocked",
  abandoned_mode_switch:  "AtRisk",
  cancelled:              "Draft",
};

const REASON_LABEL = {
  completed:              "Completed",
  audit_failure:          "Audit failure",
  abandoned_mode_switch:  "Mode switch",
  cancelled:              "Cancelled",
};

// ---------------------------------------------------------------------------
// Permanent constraint labels (spec §11.2 — 5 permanent constraints)
// ---------------------------------------------------------------------------

const CONSTRAINT_LABELS = {
  nn_03_payout_computation:    "NN-03 — AI never computes, estimates, or recalculates any payout",
  stage_19_exclusion:          "Stage 19 excluded from all agent tool schemas",
  pii_boundary:                "No PII, monetary amounts, or employee names sent to the model",
  dispute_submission_boundary: "Agent never submits disputes — comp_admin only",
  aggregate_identification:    "No individual identification in aggregate tool output",
};

// ---------------------------------------------------------------------------
// Provider display
// ---------------------------------------------------------------------------

const PROVIDER_LABEL = {
  anthropic: "Anthropic",
  openai:    "OpenAI",
};

// ---------------------------------------------------------------------------
// Data hook
// ---------------------------------------------------------------------------

function useTrustCenter() {
  const [state, setState] = useState({ data: null, loading: true, error: null });

  const load = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const res = await fetch(`${API_BASE}/agent/trust-center`, {
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail?.error?.message ?? `Error ${res.status}`);
      }
      const json = await res.json();
      setState({ data: json, loading: false, error: null });
    } catch (e) {
      setState({ data: null, loading: false, error: e.message });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return { state, refresh: load };
}

// ---------------------------------------------------------------------------
// Section: Model Configuration
// ---------------------------------------------------------------------------

function ModelConfigSection({ modelConfig }) {
  if (!modelConfig) return <LoadingRow />;

  const provider = PROVIDER_LABEL[modelConfig.model_provider] ?? modelConfig.model_provider ?? "—";
  const version  = modelConfig.model_version ?? "—";
  const keySource = modelConfig.api_key_source === "platform"
    ? "Platform managed"
    : modelConfig.api_key_source === "tenant"
      ? "Tenant configured"
      : (modelConfig.api_key_source ?? "—");

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-slate-200 px-3 py-3 dark:border-slate-700">
          <p className="text-xs text-slate-400">Provider</p>
          <p className="mt-0.5 text-sm font-semibold text-slate-800 dark:text-slate-200">
            {provider}
          </p>
        </div>
        <div className="rounded-xl border border-slate-200 px-3 py-3 dark:border-slate-700">
          <p className="text-xs text-slate-400">Model version</p>
          <p className="mt-0.5 font-mono text-sm font-medium text-slate-800 dark:text-slate-200">
            {version}
          </p>
        </div>
        <div className="rounded-xl border border-slate-200 px-3 py-3 dark:border-slate-700">
          <p className="text-xs text-slate-400">API key source</p>
          <p className="mt-0.5 text-sm text-slate-700 dark:text-slate-300">{keySource}</p>
        </div>
        <div className="rounded-xl border border-slate-200 px-3 py-3 dark:border-slate-700">
          <p className="text-xs text-slate-400">Configuration</p>
          <div className="mt-0.5">
            <Pill tone={modelConfig.is_platform_default ? "Draft" : "Ready"}>
              {modelConfig.is_platform_default ? "Platform default" : "Custom"}
            </Pill>
          </div>
        </div>
      </div>

      {modelConfig.configured_by && (
        <div className="flex items-center justify-between text-xs text-slate-400">
          <span>Configured by <span className="font-mono text-slate-500">{modelConfig.configured_by}</span></span>
          {modelConfig.configured_at && (
            <span>{formatDate(modelConfig.configured_at)}</span>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section: Agent Activity (30-day window)
// ---------------------------------------------------------------------------

function ActivitySection({ summary }) {
  if (!summary) return <LoadingRow />;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-slate-200 px-3 py-3 dark:border-slate-700">
          <p className="text-xs text-slate-400">Total sessions</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-900 dark:text-slate-100">
            {summary.total_sessions ?? 0}
          </p>
          <p className="mt-0.5 text-xs text-slate-400">last {summary.window_days ?? 30} days</p>
        </div>
        <div className="rounded-xl border border-slate-200 px-3 py-3 dark:border-slate-700">
          <p className="text-xs text-slate-400">Sessions with audit gaps</p>
          <p className={`mt-1 text-2xl font-semibold tabular-nums ${
            (summary.sessions_with_audit_gaps ?? 0) > 0
              ? "text-amber-600 dark:text-amber-400"
              : "text-slate-900 dark:text-slate-100"
          }`}>
            {summary.sessions_with_audit_gaps ?? 0}
          </p>
          <p className="mt-0.5 text-xs text-slate-400">audit_failure completions</p>
        </div>
      </div>

      {summary.proactive_alerts_sent != null && (
        <div className="rounded-xl border border-slate-200 px-3 py-2.5 dark:border-slate-700">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-500">Proactive alerts sent</span>
            <span className="font-mono text-sm font-medium text-slate-700 dark:text-slate-300">
              {summary.proactive_alerts_sent}
            </span>
          </div>
        </div>
      )}

      {summary.schema_note && (
        <InfoNote>{summary.schema_note}</InfoNote>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section: Recent Sessions
// ---------------------------------------------------------------------------

function RecentSessionsSection({ sessions }) {
  if (!sessions) return <LoadingRow />;

  if (sessions.length === 0) {
    return (
      <p className="text-sm text-slate-400">
        No agent sessions in the last 30 days.
      </p>
    );
  }

  return (
    <div className="space-y-1.5">
      {sessions.map((s) => {
        const reason = s.completion_reason ?? "unknown";
        const tone   = REASON_TONE[reason] ?? "Draft";
        const label  = REASON_LABEL[reason] ?? reason;

        return (
          <div
            key={s.agent_session_id}
            className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-3 py-2.5 dark:border-slate-700"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs text-slate-400">
                  {truncateId(s.agent_session_id)}
                </span>
                <Pill tone={tone}>{label}</Pill>
              </div>
              <div className="mt-0.5 flex items-center gap-2 text-xs text-slate-400">
                <span>{formatDateTime(s.started_at)}</span>
                {s.model_version && (
                  <>
                    <span>·</span>
                    <span className="font-mono">{s.model_version}</span>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })}

      <p className="pt-1 text-xs text-slate-400">
        Showing up to 10 most recent sessions — no context data or PII.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section: Kill Switch Detail (sidebar)
// ---------------------------------------------------------------------------

function KillSwitchDetail({ ks }) {
  if (!ks || typeof ks !== "object") {
    return <p className="text-sm text-slate-400">No kill switch data.</p>;
  }

  // Normalise to an array of scope entries for display
  let entries = [];

  if (Array.isArray(ks.scopes)) {
    entries = ks.scopes;
  } else {
    entries = Object.entries(ks)
      .filter(([k, v]) => v && typeof v === "object" && "active" in v)
      .map(([k, v]) => ({ scope: k, ...v }));
  }

  if (entries.length === 0) {
    return <p className="text-sm text-slate-400">No switch entries found.</p>;
  }

  return (
    <div className="space-y-1.5">
      {entries.map((entry, i) => {
        const isActive = entry.active === true;
        return (
          <div
            key={`${entry.scope}-${i}`}
            className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-2.5 py-2 dark:border-slate-700"
          >
            <div className="flex items-center gap-2 min-w-0">
              {isActive
                ? <XCircle className="h-3.5 w-3.5 shrink-0 text-red-500" />
                : <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
              }
              <div className="min-w-0">
                <p className="text-xs font-medium capitalize text-slate-700 dark:text-slate-300">
                  {entry.scope_value ? `${entry.scope}: ${entry.scope_value}` : entry.scope}
                </p>
                {entry.reason && (
                  <p className="truncate text-xs text-slate-400">{entry.reason}</p>
                )}
              </div>
            </div>
            <Pill tone={isActive ? "Blocked" : "Complete"}>
              {isActive ? "Active" : "Clear"}
            </Pill>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section: Permanent Constraints (sidebar)
// ---------------------------------------------------------------------------

function PermanentConstraintsSection({ constraints }) {
  if (!constraints || typeof constraints !== "object") return <LoadingRow />;

  const entries = Object.entries(constraints);
  if (entries.length === 0) {
    return <p className="text-sm text-slate-400">No constraint data.</p>;
  }

  return (
    <div className="space-y-2">
      {entries.map(([key, val]) => {
        const isActive = val?.status === "active";
        const label    = CONSTRAINT_LABELS[key] ?? key.replace(/_/g, " ");
        return (
          <div key={key} className="flex items-start gap-2">
            <CheckCircle2 className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${
              isActive ? "text-emerald-500" : "text-slate-300 dark:text-slate-600"
            }`} />
            <p className="text-xs leading-relaxed text-slate-600 dark:text-slate-400">
              {label}
            </p>
          </div>
        );
      })}
      <p className="pt-1 text-xs text-slate-400">
        Permanent constraints cannot be disabled by any user or kill switch.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section: Tool Manifest (sidebar)
// ---------------------------------------------------------------------------

function ToolManifestSection({ manifest }) {
  if (!manifest) return <LoadingRow />;

  if (manifest.error) {
    return <ErrorNote message={`Manifest unavailable: ${manifest.error}`} />;
  }

  return (
    <div className="space-y-2">
      {[
        { label: "Version",     value: manifest.manifest_version ?? "—" },
        { label: "Tool count",  value: manifest.tool_count ?? "—" },
        { label: "Algorithm",   value: manifest.signature_algorithm ?? "HMAC-SHA256" },
        { label: "Signed",      value: manifest.signed_at ? formatDate(manifest.signed_at) : "—" },
      ].map(({ label, value }) => (
        <div key={label} className="flex items-center justify-between">
          <span className="text-xs text-slate-400">{label}</span>
          <span className="font-mono text-xs font-medium text-slate-700 dark:text-slate-300">
            {value}
          </span>
        </div>
      ))}
      <div className="flex items-center gap-1.5 pt-1">
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
        <span className="text-xs text-emerald-600 dark:text-emerald-400">Signature verified</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section: Data Boundary (sidebar)
// ---------------------------------------------------------------------------

function DataBoundarySection({ boundary }) {
  if (!boundary) return <LoadingRow />;

  return (
    <div className="space-y-3">
      {boundary.sent_to_model?.length > 0 && (
        <div>
          <p className="mb-1.5 text-xs font-medium text-slate-500">Sent to model</p>
          <div className="space-y-1">
            {boundary.sent_to_model.map((item, i) => (
              <div key={i} className="flex items-start gap-1.5">
                <ChevronRight className="mt-0.5 h-3 w-3 shrink-0 text-slate-300" />
                <p className="text-xs text-slate-600 dark:text-slate-400">{item}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {boundary.never_sent_to_model?.length > 0 && (
        <div>
          <div className="mb-1.5 flex items-center gap-1.5">
            <EyeOff className="h-3.5 w-3.5 text-slate-400" />
            <p className="text-xs font-medium text-slate-500">Never sent to model</p>
          </div>
          <div className="space-y-1">
            {boundary.never_sent_to_model.map((item, i) => (
              <div key={i} className="flex items-start gap-1.5">
                <XCircle className="mt-0.5 h-3 w-3 shrink-0 text-red-400" />
                <p className="text-xs text-slate-600 dark:text-slate-400">{item}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function TrustCenter() {
  const { state, refresh } = useTrustCenter();
  const { data, loading, error } = state;

  // Derived sections
  const modelConfig  = data?.model_config        ?? null;
  const activity     = data?.activity_summary     ?? null;
  const governance   = data?.governance_posture   ?? null;
  const boundary     = data?.data_boundary        ?? null;
  const sessions     = data?.recent_sessions      ?? null;

  const killSwitch   = governance?.kill_switch         ?? null;
  const constraints  = governance?.permanent_constraints ?? null;
  const manifest     = governance?.tool_manifest        ?? null;

  const posture = killSwitch ? deriveKillSwitchPosture(killSwitch) : "clear";

  const postureLabel = {
    clear:                 "Clear",
    restricted:            "Restricted",
    platform_restricted:   "Platform Restricted",
  }[posture] ?? "Unknown";

  const postureTone = {
    clear:               "Complete",
    restricted:          "AtRisk",
    platform_restricted: "Blocked",
  }[posture] ?? "Draft";

  return (
    <div className="space-y-4">

      {/* ------------------------------------------------------------------ */}
      {/* Page header                                                          */}
      {/* ------------------------------------------------------------------ */}
      <Card
        title="Agent Trust Center"
        action={
          <div className="flex items-center gap-2">
            {!loading && data && (
              <Pill tone={postureTone}>
                Posture: {postureLabel}
              </Pill>
            )}
            <button
              onClick={refresh}
              disabled={loading}
              className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-100 disabled:opacity-40 dark:text-slate-400 dark:hover:bg-slate-800"
              title="Refresh"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              {loading ? "Loading…" : "Refresh"}
            </button>
          </div>
        }
      >
        <p className="text-sm text-slate-500">
          Governance management surface — model configuration, kill switches, and agent activity.
          This page makes no AI calls and contains no computed estimates.
          {data?.tenant_id && (
            <span className="ml-1 font-mono text-xs text-slate-400">
              Tenant: {truncateId(data.tenant_id)}
            </span>
          )}
        </p>
        {error && <div className="mt-3"><ErrorNote message={error} /></div>}
      </Card>

      {/* ------------------------------------------------------------------ */}
      {/* Two-column body                                                       */}
      {/* ------------------------------------------------------------------ */}
      <div className="grid gap-4 lg:grid-cols-3">

        {/* ═══════════════════════════════════════════════════════════════════
            Main column — Model Config · Activity · Recent Sessions
        ═══════════════════════════════════════════════════════════════════ */}
        <div className="space-y-4 lg:col-span-2">

          {/* Model Configuration */}
          <Card
            title="Model Configuration"
            action={<KeyRound className="h-4 w-4 text-slate-400" />}
          >
            {loading
              ? <LoadingRow />
              : <ModelConfigSection modelConfig={modelConfig} />
            }
          </Card>

          {/* Agent Activity */}
          <Card
            title="Agent Activity"
            action={
              <span className="text-xs text-slate-400">
                Last {activity?.window_days ?? 30} days
              </span>
            }
          >
            {loading
              ? <LoadingRow />
              : <ActivitySection summary={activity} />
            }
          </Card>

          {/* Recent Sessions */}
          <Card
            title="Recent Agent Sessions"
            action={<Activity className="h-4 w-4 text-slate-400" />}
          >
            {loading
              ? <LoadingRow />
              : <RecentSessionsSection sessions={sessions} />
            }
          </Card>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════
            Sidebar — Kill Switch · Constraints · Manifest · Data Boundary
        ═══════════════════════════════════════════════════════════════════ */}
        <div className="space-y-4">

          {/* Kill Switch Status */}
          <Card
            title="Kill Switch Status"
            action={<Shield className="h-4 w-4 text-slate-400" />}
          >
            {loading ? (
              <LoadingRow />
            ) : (
              <div className="space-y-3">
                <KillSwitchBadge posture={posture} />
                {killSwitch && <KillSwitchDetail ks={killSwitch} />}
              </div>
            )}
          </Card>

          {/* Permanent Constraints */}
          <Card
            title="Permanent Constraints"
            action={<ShieldCheck className="h-4 w-4 text-emerald-500" />}
          >
            {loading
              ? <LoadingRow />
              : <PermanentConstraintsSection constraints={constraints} />
            }
          </Card>

          {/* Tool Manifest */}
          <Card
            title="Tool Manifest"
            action={<Server className="h-4 w-4 text-slate-400" />}
          >
            {loading
              ? <LoadingRow />
              : <ToolManifestSection manifest={manifest} />
            }
          </Card>

          {/* AI Governance — S1034: AIGovernancePanel (CA delegation + operating mode) */}
          <AIGovernancePanel />

          {/* Data Boundary */}
          <Card
            title="Data Boundary"
            action={<Database className="h-4 w-4 text-slate-400" />}
          >
            {loading
              ? <LoadingRow />
              : boundary
                ? <DataBoundarySection boundary={boundary} />
                : <p className="text-sm text-slate-400">No boundary data.</p>
            }
          </Card>
        </div>
      </div>
    </div>
  );
}
